<template>
  <b-modal
    id="subscription-details-modal"
    ref="modal"
    size="xl"
    hide-header
    hide-footer
    body-class="p-0"
    dialog-class="sub-modal-dialog"
  >
    <!-- ── LOADING ── -->
    <div v-if="loading" class="sub-loading">
      <div class="sub-spinner"></div>
      <p>Loading subscription…</p>
    </div>

    <!-- ── MAIN ── -->
    <div v-else-if="subscription" class="sub-root">

      <!-- MODAL HEADER -->
      <div class="sub-header">
        <div class="sub-header__left">
          <span class="sub-header__eyebrow">Subscription</span>
          <h2 class="sub-header__ref">{{ subscription.subscription_order || receiptNumber }}</h2>
        </div>
        <div class="sub-header__right">
          <span class="sub-status-chip" :class="`sub-status-chip--${subscription.status}`">
            {{ subscription.status }}
          </span>

          <!-- ── BALANCE DUE CHIP — shown when underpaid ── -->
          <span v-if="isUnderPaid" class="sub-balance-chip">
            KES {{ formatAmount(balanceDue) }} due
          </span>

          <div class="sub-action-group">
            <!-- Print -->
            <button
              class="sub-icon-btn"
              :class="{ 'sub-icon-btn--loading': hasTims }"
              :disabled="hasTims"
              title="Print 80mm receipt"
              @click="printReceipt"
            >
              <i v-if="hasTims" class="bi bi-arrow-repeat spin-icon"></i>
              <i v-else class="bi bi-printer"></i>
            </button>
            <!-- PDF -->
            <button
              class="sub-icon-btn"
              :class="{ 'sub-icon-btn--loading': pdfGenerating }"
              :disabled="pdfGenerating"
              title="Download PDF"
              @click="downloadPdf"
            >
              <i v-if="pdfGenerating" class="bi bi-arrow-repeat spin-icon"></i>
              <i v-else class="bi bi-file-pdf"></i>
            </button>
            <!-- Renew -->
            <button
              v-if="can('subscriptions.renew')"
              class="sub-icon-btn"
              title="Renew subscription"
              @click="renewSubscription"
            >
              <i class="bi bi-arrow-repeat"></i>
            </button>
            <!-- More -->
            <div class="dropdown">
              <button class="sub-icon-btn" data-toggle="dropdown" title="More actions">
                <i class="bi bi-three-dots-vertical"></i>
              </button>
              <div class="dropdown-menu dropdown-menu-right sub-dropdown">
                <div class="sub-dropdown__label">Subscription</div>
                <a
                  v-if="can('subscriptions.renew')"
                  class="dropdown-item"
                  href="#"
                  @click.prevent="renewSubscription"
                >
                  <i class="bi bi-arrow-repeat"></i> Renew
                </a>
                <a
                  v-if="can('subscriptions.cancel')"
                  class="dropdown-item"
                  href="#"
                  @click.prevent="cancelSubscription"
                >
                  <i class="bi bi-slash-circle"></i> Cancel
                </a>
                <a
                  v-if="can('subscriptions.edit')"
                  class="dropdown-item"
                  href="#"
                  @click.prevent="openAddNoteModal()"
                >
                  <i class="bi bi-sticky"></i> Add Note
                </a>
                <a class="dropdown-item" href="#" @click.prevent="toggleAutoRenew">
                  <i :class="subscription.is_auto_renew ? 'bi bi-toggle-on' : 'bi bi-toggle-off'"></i>
                  {{ subscription.is_auto_renew ? 'Disable' : 'Enable' }} Auto-Renew
                </a>
                <div class="dropdown-divider"></div>
                <!-- ── ADD PAYMENT — only when underpaid + has permission ── -->
                <a
                  v-if="isUnderPaid && can('subscriptions.manage_payments')"
                  class="dropdown-item"
                  href="#"
                  @click.prevent="openPaymentModal"
                >
                  <i class="bi bi-cash-coin"></i> Add Payment
                  <span class="sub-balance-chip sub-balance-chip--sm ml-1">
                    KES {{ formatAmount(balanceDue) }} due
                  </span>
                </a>
                <div v-if="isUnderPaid" class="dropdown-divider"></div>
                <div class="sub-dropdown__label">Customer</div>
                <a class="dropdown-item" href="#" @click.prevent="viewCustomer">
                  <i class="bi bi-person"></i> View Customer
                </a>
                <a
                  v-if="can('subscriptions.view_financials')"
                  class="dropdown-item"
                  href="#"
                  @click.prevent="$refs.statementModal.show(subscription.customer_id)"
                >
                  <i class="bi bi-file-earmark-text"></i> Statement
                </a>
                <a class="dropdown-item" href="#" @click.prevent="copyMemberLink">
                  <i class="bi bi-link-45deg"></i> Copy member app link
                </a>
                <a
                  v-if="can('subscriptions.edit') && subscription.customer && subscription.customer.phone"
                  class="dropdown-item"
                  href="#"
                  @click.prevent="sendMemberLink"
                >
                  <i class="bi bi-chat-text"></i> SMS member app link
                </a>
                <div class="dropdown-divider"></div>
                <div class="sub-dropdown__label">Fiscal</div>
                <a
                  v-if="can('subscriptions.edit') && canGenerateFiscalReceipt"
                  class="dropdown-item"
                  href="#"
                  :class="{ disabled: fiscalReceiptLoading }"
                  @click.prevent="generateFiscalReceipt"
                >
                  <i class="bi bi-receipt"></i>
                  {{ fiscalReceiptLoading ? 'Processing…' : (subscription.fiscal_transaction && subscription.fiscal_transaction.status === 'failed' ? 'Retry Fiscal Receipt' : 'Generate Fiscal Receipt') }}
                </a>
                <a
                  v-if="can('subscriptions.cancel')"
                  class="dropdown-item"
                  href="#"
                  :class="{ disabled: issuingCreditNote }"
                  @click.prevent="issueCreditNote"
                >
                  <i class="bi bi-receipt-cutoff"></i>
                  {{ issuingCreditNote ? 'Issuing…' : 'Issue Credit Note' }}
                </a>
                <div class="dropdown-divider"></div>
                <a
                  v-if="can('subscriptions.delete')"
                  class="dropdown-item text-danger"
                  href="#"
                  @click.prevent="deleteSubscription"
                >
                  <i class="bi bi-trash"></i> Delete
                </a>
              </div>
            </div>
            <!-- Close -->
            <button class="sub-icon-btn sub-icon-btn--close" title="Close" @click="$refs.modal.hide()">
              <i class="bi bi-x"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- FISCAL STATUS BAR -->
      <div v-if="fiscalLoadingStatus" class="sub-fiscal-bar">
        <i class="bi bi-arrow-repeat spin-icon"></i>
        <span>{{ fiscalLoadingStatus }}</span>
      </div>

      <!-- EXPIRY WARNING BAND -->
      <div v-if="subscription.status === 'active' && daysUntilExpiry <= 7" class="sub-warn-band">
        <i class="bi bi-exclamation-triangle"></i>
        Expires in <strong>{{ daysUntilExpiry }} day{{ daysUntilExpiry !== 1 ? 's' : '' }}</strong>
        <span v-if="subscription.is_auto_renew" class="sub-warn-band__badge">Auto-Renew On</span>
      </div>

      <!-- UNDERPAID WARNING BAND -->
      <div v-if="isUnderPaid" class="sub-underpaid-band">
        <i class="bi bi-exclamation-circle"></i>
        Payment incomplete — <strong>KES {{ formatAmount(balanceDue) }}</strong> still outstanding.
        <button
          v-if="can('subscriptions.manage_payments')"
          class="sub-underpaid-band__btn"
          @click="openPaymentModal"
        >
          Add Payment
        </button>
      </div>

      <!-- PACKAGE STRIP -->
      <div class="sub-pkg-strip">
        <div class="sub-pkg-strip__item">
          <span class="sub-pkg-strip__label">Package</span>
          <span class="sub-pkg-strip__value">{{ subscription.package.name }}</span>
        </div>
        <div class="sub-pkg-strip__sep">·</div>
        <div class="sub-pkg-strip__item">
          <span class="sub-pkg-strip__label">Start</span>
          <span class="sub-pkg-strip__value">{{ formatDate(subscription.start_date,'DD MMM YYYY') }}</span>
        </div>
        <div class="sub-pkg-strip__sep"><i class="bi bi-arrow-right"></i></div>
        <div class="sub-pkg-strip__item">
          <span class="sub-pkg-strip__label">End</span>
          <span class="sub-pkg-strip__value">{{ formatDate(subscription.end_date,'DD MMM YYYY') }}</span>
        </div>
        <div class="sub-pkg-strip__sep">·</div>
        <div class="sub-pkg-strip__item">
          <span class="sub-pkg-strip__label">Duration</span>
          <span class="sub-pkg-strip__value sub-pkg-strip__value--accent">
            {{ subscription.package.duration_value }} {{ subscription.package.duration_unit }}(s)
          </span>
        </div>
        <div class="sub-pkg-strip__sep">·</div>
        <div class="sub-pkg-strip__item">
          <span class="sub-pkg-strip__label">Price</span>
          <span class="sub-pkg-strip__value">KES {{ formatAmount(subscription.package.price) }}</span>
        </div>
      </div>

      <!-- BODY GRID -->
      <div class="sub-body">

        <!-- LEFT COLUMN -->
        <div class="sub-body__main">

          <!-- Customer card -->
          <div class="card">
            <div class="card-body">
              <div class="h3"><span>Customer</span></div>
              <div>
                <div class="sub-guest-block__name">
                  {{ subscription.customer?.full_name || '—' }}
                </div>
                <div v-if="subscription.customer?.phone">
                  <i class="bi bi-telephone"></i>
                  {{ subscription.customer.phone }}
                </div>
                <div v-if="subscription.customer?.email">
                  <i class="bi bi-envelope"></i>
                  {{ subscription.customer.email }}
                </div>
                <div v-if="subscription.customer?.krapin">
                  <i class="bi bi-fingerprint"></i>
                  PIN: {{ subscription.customer.krapin }}
                </div>
              </div>
            </div>
          </div>

          <!-- Subscription info card -->
          <div class="sub-card">
            <div class="sub-card__header">
              <span class="sub-card__label">Subscription Details</span>
            </div>
            <div class="sub-fields-grid">
              <div class="sub-field">
                <span class="sub-field__key">Reference</span>
                <span class="sub-field__val sub-field__val--mono">{{ subscription.subscription_order || receiptNumber }}</span>
              </div>
              <div class="sub-field">
                <span class="sub-field__key">Status</span>
                <span class="sub-status-chip sub-status-chip--sm" :class="`sub-status-chip--${subscription.status}`">
                  {{ subscription.status }}
                </span>
              </div>
              <div class="sub-field">
                <span class="sub-field__key">Start Date</span>
                <span class="sub-field__val">{{ formatDate(subscription.start_date,'DD MMM YYYY') }}</span>
              </div>
              <div class="sub-field">
                <span class="sub-field__key">End Date</span>
                <span class="sub-field__val">{{ formatDate(subscription.end_date,'DD MMM YYYY') }}</span>
              </div>
              <div class="sub-field">
                <span class="sub-field__key">Last Renewed</span>
                <span class="sub-field__val">{{ subscription.last_renewed_at ? formatDate(subscription.last_renewed_at,'DD MMM YYYY') : 'Never' }}</span>
              </div>
              <div class="sub-field">
                <span class="sub-field__key">Next Renewal</span>
                <span class="sub-field__val">{{ subscription.next_renewal_date ? formatDate(subscription.next_renewal_date,'DD MMM YYYY') : 'N/A' }}</span>
              </div>
              <div class="sub-field">
                <span class="sub-field__key">Auto-Renew</span>
                <span class="sub-field__val">
                  <span class="sub-toggle-badge" :class="subscription.is_auto_renew ? 'sub-toggle-badge--on' : 'sub-toggle-badge--off'">
                    {{ subscription.is_auto_renew ? 'Enabled' : 'Disabled' }}
                  </span>
                </span>
              </div>
              <div class="sub-field sub-field--full">
                <span class="sub-field__key">
                  Notes
                  <a
                    v-if="can('subscriptions.edit') && !editingNotes"
                    href="#"
                    class="ml-2"
                    style="font-size:11px;font-weight:400;text-transform:none;letter-spacing:0"
                    @click.prevent="startEditNotes"
                  ><i class="bi bi-pencil"></i> Edit</a>
                </span>
                <div v-if="editingNotes">
                  <b-form-textarea
                    v-model="notesEdit"
                    rows="3"
                    size="sm"
                    class="mt-1"
                  />
                  <div class="mt-1 d-flex gap-2">
                    <b-button size="sm" variant="primary" :disabled="savingNotes" @click="saveNotes">
                      <b-spinner v-if="savingNotes" small class="mr-1" />Save
                    </b-button>
                    <b-button size="sm" variant="outline-secondary" class="ml-1" @click="cancelEditNotes">Cancel</b-button>
                  </div>
                </div>
                <span v-else class="sub-field__val">{{ subscription.notes || '—' }}</span>
              </div>
            </div>
          </div>

          <!-- Notes log card -->
          <div v-if="can('subscriptions.view')" class="sub-card">
            <div class="sub-card__header">
              <span class="sub-card__label">Notes</span>
              <button
                v-if="can('subscriptions.edit')"
                class="sub-card__action-btn"
                @click="openAddNoteModal()"
              >
                <i class="bi bi-plus mr-1"></i>Add Note
              </button>
            </div>
            <div v-if="subscriptionNotes.length">
              <div
                v-for="(n, i) in subscriptionNotes"
                :key="n.id"
                class="sub-note-row"
                :class="{ 'sub-payment-row--alt': i % 2 !== 0 }"
              >
                <div class="sub-note-row__body">{{ n.note }}</div>
                <div class="sub-note-row__meta">
                  <span class="sub-method-badge">{{ n.note_type }}</span>
                  <span class="text-muted" style="font-size:11px">
                    {{ n.created_by ? n.created_by.name : '—' }}
                  </span>
                  <span class="text-muted" style="font-size:11px">
                    {{ formatDate(n.created_at, 'DD MMM YYYY, HH:mm') }}
                  </span>
                </div>
              </div>
            </div>
            <p v-else class="sub-empty-text">No notes yet.</p>
          </div>

          <!-- ══════════════════════════════════════════════════════
               PAYMENT HISTORY + ADD PAYMENT PANEL
               ══════════════════════════════════════════════════════ -->
          <div class="sub-card">
            <div class="sub-card__header">
              <span class="sub-card__label">Payment History</span>
              <button
                v-if="isUnderPaid && can('subscriptions.manage_payments')"
                class="sub-card__action-btn"
                @click="openPaymentModal"
              >
                <i class="bi bi-plus-circle mr-1"></i>Add Payment
              </button>
            </div>

            <!-- Existing payment rows -->
            <div v-if="subscription.payments && subscription.payments.length">
              <div
                v-for="(payment, i) in subscription.payments"
                :key="payment.id"
                class="sub-payment-row"
                :class="{ 'sub-payment-row--alt': i % 2 !== 0 }"
              >
                <div class="sub-payment-row__left">
                  <span class="sub-payment-row__ref sub-field__val--mono">{{ payment.reference || '—' }}</span>
                  <span class="sub-payment-row__date">{{ payment.created_at }}</span>
                </div>
                <div class="sub-payment-row__right">
                  <span class="sub-payment-row__amount">KES {{ formatAmount(payment.amount) }}</span>
                  <span class="sub-method-badge">{{ (payment.payment_type && payment.payment_type.payment_method) || payment.method || '—' }}</span>
                  <span
                    class="sub-status-chip sub-status-chip--sm"
                    :class="payment.status === 'completed' ? 'sub-status-chip--active' : 'sub-status-chip--pending'"
                  >
                    {{ payment.status }}
                  </span>
                </div>
              </div>
            </div>
            <p v-else class="sub-empty-text">No payment history available.</p>

            <!-- /payment rows -->

          </div>

          <!-- Installment Schedule card — only for users with payment permission -->
          <div v-if="can('subscriptions.manage_payments')" class="sub-card">
            <div class="sub-card__header">
              <span class="sub-card__label">Installment Schedule</span>
              <div class="d-flex align-items-center gap-1">
                <b-spinner v-if="installmentsLoading" small />
                <button
                  v-if="can('subscriptions.manage_payments') && !installments.length && !installmentsLoading"
                  class="sub-card__action-btn"
                  @click="openCreateScheduleModal"
                >
                  <i class="bi bi-plus mr-1"></i>Create Schedule
                </button>
                <button
                  v-else-if="installments.length && can('subscriptions.manage_payments')"
                  class="sub-card__action-btn sub-card__action-btn--muted"
                  @click="loadInstallments"
                  title="Refresh"
                >
                  <i class="bi bi-arrow-clockwise"></i>
                </button>
              </div>
            </div>

            <!-- Installment rows -->
            <div v-if="installments.length">
              <!-- Totals bar -->
              <div class="inst-totals-bar">
                <span>Total Due: <strong>KES {{ fmtInst(installmentTotals.amount_due) }}</strong></span>
                <span>Paid: <strong class="text-success">KES {{ fmtInst(installmentTotals.amount_paid) }}</strong></span>
                <span v-if="installmentTotals.balance > 0">
                  Balance: <strong class="text-danger">KES {{ fmtInst(installmentTotals.balance) }}</strong>
                </span>
                <span v-else class="text-success"><i class="bi bi-check-circle mr-1"></i>Fully Paid</span>
              </div>

              <div
                v-for="(inst, i) in installments"
                :key="inst.id"
                class="inst-row"
                :class="{ 'inst-row--alt': i % 2 !== 0, [`inst-row--${inst.status}`]: true }"
              >
                <div class="inst-row__left">
                  <span class="inst-row__num">#{{ inst.installment_number }}</span>
                  <span class="inst-row__date">Due: {{ inst.due_date }}</span>
                  <span v-if="inst.notes" class="inst-row__notes">{{ inst.notes }}</span>
                </div>
                <div class="inst-row__right">
                  <span class="inst-row__amounts">
                    <span class="inst-row__paid">KES {{ fmtInst(inst.amount_paid) }}</span>
                    <span class="inst-row__sep">/</span>
                    <span class="inst-row__due">KES {{ fmtInst(inst.amount_due) }}</span>
                  </span>
                  <span class="inst-status-badge" :class="`inst-status-badge--${inst.status}`">
                    {{ inst.status }}
                  </span>
                  <button
                    v-if="inst.status !== 'paid'"
                    class="sub-card__action-btn inst-pay-btn"
                    @click="openInstPayModal(inst)"
                  >
                    Pay
                  </button>
                </div>
              </div>
            </div>
            <p v-else-if="!installmentsLoading" class="sub-empty-text">
              No installment schedule. Create one to track partial payments.
            </p>
          </div>

          <!-- Renewal History card -->
          <div v-if="subscription.renewals && subscription.renewals.length" class="sub-card">
            <div class="sub-card__header">
              <span class="sub-card__label">Renewal History</span>
              <span class="text-muted" style="font-size:12px">{{ subscription.renewals.length }} renewal(s)</span>
            </div>
            <div
              v-for="(r, i) in subscription.renewals"
              :key="r.id"
              class="sub-payment-row"
              :class="{ 'sub-payment-row--alt': i % 2 !== 0 }"
            >
              <div class="sub-payment-row__left">
                <span class="sub-payment-row__ref sub-field__val--mono">{{ r.renewal_order }}</span>
                <span class="sub-payment-row__date">
                  {{ formatDate(r.start_date,'DD MMM YYYY') }} → {{ formatDate(r.end_date,'DD MMM YYYY') }}
                </span>
              </div>
              <div class="sub-payment-row__right">
                <span class="sub-payment-row__amount">KES {{ formatAmount(r.amount_due) }}</span>
                <span v-if="r.discount_amount > 0" class="sub-method-badge" style="color:#28a745">
                  −KES {{ formatAmount(r.discount_amount) }}
                </span>
                <span v-if="parseFloat(r.amount_paid) < parseFloat(r.amount_due)" class="sub-method-badge" style="background:#fff3cd;color:#856404">
                  KES {{ formatAmount(r.amount_due - r.amount_paid) }} due
                </span>
                <span v-if="r.renewed_by" class="text-muted" style="font-size:11px">
                  {{ r.renewed_by.name }}
                </span>
              </div>
            </div>
          </div>

          <!-- SMS logs card -->
          <div class="sub-card">
            <div class="sub-card__header">
              <span class="sub-card__label">SMS Notifications</span>
            </div>
            <div v-if="subscription.sms_logs && subscription.sms_logs.length">
              <div
                v-for="(sms, i) in subscription.sms_logs"
                :key="sms.id"
                class="sub-sms-row"
                :class="{ 'sub-sms-row--alt': i % 2 !== 0 }"
              >
                <div class="sub-sms-row__left">
                  <span class="sub-method-badge sub-method-badge--sms">{{ formatMessageType(sms.message_type) }}</span>
                  <span class="sub-payment-row__date">{{ sms.created_at }}</span>
                </div>
                <div class="sub-sms-row__right">
                  <span
                    class="sub-status-chip sub-status-chip--sm"
                    :class="sms.status === 'sent' ? 'sub-status-chip--active' : 'sub-status-chip--cancelled'"
                  >
                    {{ sms.status }}
                  </span>
                  <span class="sub-sms-row__msg">{{ sms.message }}</span>
                </div>
              </div>
            </div>
            <p v-else class="sub-empty-text">No SMS notifications sent.</p>
          </div>

          <!-- Sessions card -->
          <div v-if="can('subscriptions.checkin')" class="sub-card">
            <div class="sub-card__header">
              <span class="sub-card__label">Sessions</span>
              <span class="text-muted" style="font-size:12px">Last 30</span>
            </div>
            <div v-if="subscription.sessions && subscription.sessions.length">
              <div
                v-for="(sess, i) in subscription.sessions"
                :key="sess.id"
                class="sub-session-row"
                :class="{ 'sub-payment-row--alt': i % 2 !== 0 }"
              >
                <div class="sub-session-row__left">
                  <span class="sub-field__val--mono" style="font-size:12px">{{ formatDate(sess.entered_at, 'DD MMM YYYY, HH:mm') }}</span>
                  <span v-if="sess.exited_at" class="text-muted" style="font-size:11px">
                    → {{ formatDate(sess.exited_at, 'HH:mm') }}
                    <span class="ml-1">({{ sessionDuration(sess) }})</span>
                  </span>
                  <span v-else class="badge badge-success" style="font-size:10px">Inside</span>
                </div>
                <div class="sub-session-row__right">
                  <span class="sub-method-badge">{{ sess.check_in_type }}</span>
                  <span v-if="sess.confirmed_by_user" class="text-muted" style="font-size:11px">
                    {{ sess.confirmed_by_user.name }}
                  </span>
                </div>
              </div>
            </div>
            <p v-else class="sub-empty-text">No sessions recorded.</p>
          </div>

        </div>

        <!-- RIGHT COLUMN — unchanged -->
        <div class="sub-body__aside">

          <div class="sub-stat-chips">
            <div class="sub-stat-chip">
              <span class="sub-stat-chip__val">{{ daysActive }}</span>
              <span class="sub-stat-chip__lbl">days active</span>
            </div>
            <div class="sub-stat-chip">
              <span
                class="sub-stat-chip__val"
                :class="subscription.status === 'active' && daysUntilExpiry <= 7 ? 'sub-stat-chip__val--warn' : ''"
              >
                {{ subscription.status === 'active' ? daysUntilExpiry : '—' }}
              </span>
              <span class="sub-stat-chip__lbl">days left</span>
            </div>
            <div class="sub-stat-chip">
              <span class="sub-stat-chip__val">{{ renewalCount }}</span>
              <span class="sub-stat-chip__lbl">renewals</span>
            </div>
          </div>

          <!-- Financials card -->
          <div class="sub-card sub-card--payment">
            <div class="sub-card__header">
              <span class="sub-card__label">Financials</span>
            </div>
            <div class="sub-payment-stack">
              <!-- Unit price × participant count -->
              <div class="sub-payment-line">
                <span class="sub-payment-line__label">
                  {{ subscription.package.name }}
                  <span v-if="participantCount > 1" class="text-muted" style="font-size:11px">
                    ({{ participantCount }} participants)
                  </span>
                </span>
                <span class="sub-payment-line__val">
                  {{ participantCount }} × KES {{ formatAmount(subscription.package.price) }}
                </span>
              </div>
              <!-- Gross subtotal — shown only when there are multiple participants or a discount -->
              <div v-if="participantCount > 1 || discountAmount > 0" class="sub-payment-line sub-payment-line--tax">
                <span class="sub-payment-line__label">Subtotal</span>
                <span class="sub-payment-line__val">KES {{ formatAmount(grossSubtotal) }}</span>
              </div>
              <!-- Discount line -->
              <div v-if="discountAmount > 0" class="sub-payment-line sub-payment-line--tax" style="color:var(--bs-success)">
                <span class="sub-payment-line__label">
                  Discount
                  <em v-if="subscription.discount_reason" style="font-size:10px">({{ subscription.discount_reason }})</em>
                </span>
                <span class="sub-payment-line__val">&#8722; KES {{ formatAmount(discountAmount) }}</span>
              </div>
              <!-- Tax breakdown from snapshot -->
              <template v-if="subscription.tax_snapshot && subscription.tax_snapshot.length">
                <div
                  v-for="tax in subscription.tax_snapshot"
                  :key="tax.id"
                  class="sub-payment-line sub-payment-line--tax"
                >
                  <span class="sub-payment-line__label">
                    {{ tax.name }} ({{ tax.perc != null ? tax.perc : tax.rate }}%)
                    <em style="font-size:10px">incl.</em>
                  </span>
                  <span class="sub-payment-line__val">KES {{ formatAmount(calculateTaxAmount(tax)) }}</span>
                </div>
              </template>
              <div class="sub-payment-divider"></div>
              <div class="sub-payment-line sub-payment-line--total">
                <span class="sub-payment-line__label">Total</span>
                <span class="sub-payment-line__val">KES {{ formatAmount(calculateTotal()) }}</span>
              </div>
              <div class="sub-payment-divider"></div>
              <div class="sub-payment-line sub-payment-line--paid">
                <span class="sub-payment-line__label">Total Paid</span>
                <span class="sub-payment-line__val">KES {{ formatAmount(runningTotalPaid) }}</span>
              </div>
              <!-- Balance due line — only when underpaid -->
              <div v-if="isUnderPaid" class="sub-payment-line sub-payment-line--balance-due">
                <span class="sub-payment-line__label">Balance Due</span>
                <span class="sub-payment-line__val sub-payment-line__val--due">
                  KES {{ formatAmount(balanceDue) }}
                </span>
              </div>
            </div>
          </div>

          <!-- Tax snapshot -->
          <div v-if="subscription.tax_snapshot && subscription.tax_snapshot.length" class="sub-card">
            <div class="sub-card__header">
              <span class="sub-card__label">Tax Snapshot</span>
            </div>
            <div class="sub-payment-stack" style="padding:10px 14px">
              <div v-for="tax in subscription.tax_snapshot" :key="tax.id" class="sub-payment-line">
                <span class="sub-payment-line__label">
                  {{ tax.name }} · {{ tax.perc != null ? tax.perc : tax.rate }}%
                </span>
                <span class="sub-method-badge">{{ tax.type }}</span>
                <span v-if="tax.amount" class="text-muted" style="font-size:11px">
                  KES {{ formatAmount(tax.amount) }}
                </span>
              </div>
            </div>
          </div>

        </div>
      </div>

    </div>

    <!-- ================================================================
         ADD PAYMENT MODAL
         ================================================================ -->
    <b-modal
      ref="paymentModal"
      title="Add Payment"
      size="md"
      hide-footer
      @hidden="resetPaymentModal"
    >
      <template v-if="subscription">

      <!-- Amount summary -->
      <b-card class="mb-3 border-0 bg-light">
        <b-row class="text-center">
          <b-col cols="12" sm="4" class="mb-2 mb-sm-0">
            <p class="text-muted mb-1">Amount Due</p>
            <h5 class="font-weight-bold mb-0">KES {{ formatAmount(calculateTotal()) }}</h5>
          </b-col>
          <b-col cols="12" sm="4" class="mb-2 mb-sm-0">
            <p class="text-muted mb-1">Total Paid</p>
            <h5 class="font-weight-bold mb-0 text-primary">KES {{ formatAmount(runningTotalPaid) }}</h5>
          </b-col>
          <b-col cols="12" sm="4" class="mb-2 mb-sm-0">
            <p class="text-muted mb-1">
              {{ runningRemaining > 0 ? 'Remaining' : runningOverpayment > 0 ? 'Overpayment' : 'Settled' }}
            </p>
            <h5
              class="font-weight-bold mb-0"
              :class="runningRemaining > 0 ? 'text-danger' : runningOverpayment > 0 ? 'text-primary' : 'text-secondary'"
            >
              <span v-if="runningRemaining > 0">KES {{ formatAmount(runningRemaining) }}</span>
              <span v-else-if="runningOverpayment > 0">KES {{ formatAmount(runningOverpayment) }}</span>
              <span v-else><i class="fas fa-check"></i></span>
            </h5>
          </b-col>
        </b-row>
      </b-card>

      <!-- Payments posted this session -->
      <div v-if="newPayments.length" class="mb-3">
        <p class="text-muted font-weight-bold mb-1">PAYMENTS RECORDED</p>
        <div
          v-for="(p, i) in newPayments"
          :key="i"
          class="d-flex justify-content-between border-bottom py-1"
        >
          <span class="text-muted">{{ p.method_label }}</span>
          <strong>KES {{ formatAmount(p.amount) }}</strong>
        </div>
      </div>

      <!-- Payment input — shown while balance remains -->
      <div v-if="runningRemaining > 0">
        <b-form-group label="Payment Method *">
          <b-form-select
            v-model="paymentLine.payment_type_id"
            :options="paymentMethodsWithWallet"
            :state="paymentErrors.payment_type_id ? false : null"
          />
          <b-form-invalid-feedback>{{ paymentErrors.payment_type_id }}</b-form-invalid-feedback>
        </b-form-group>

        <b-form-group label="Amount (KES) *">
          <b-form-input
            v-model.number="paymentLine.amount"
            type="number"
            min="0.01"
            :placeholder="formatAmount(runningRemaining)"
            :state="paymentErrors.amount ? false : null"
            @keyup.enter="postPaymentLine"
          />
          <b-form-invalid-feedback>{{ paymentErrors.amount }}</b-form-invalid-feedback>
        </b-form-group>

        <!-- Wallet balance hint when wallet is selected -->
        <div v-if="isWalletSelected" class="alert alert-secondary py-2 mb-3">
          <i class="fas fa-wallet mr-1"></i>
          Available wallet balance: <strong>KES {{ formatAmount(walletBalance) }}</strong>
        </div>

        <b-button
          variant="primary"
          block
          :disabled="postingPayment || !paymentLine.payment_type_id || !paymentLine.amount"
          @click="postPaymentLine"
        >
          <b-spinner v-if="postingPayment" class="mr-1" />
          Add Payment
        </b-button>
      </div>

      <!-- Overpayment prompt -->
      <div v-if="runningOverpayment > 0 && !overpaymentHandled">
        <b-card class="border-0 bg-light mb-3">
          <p class="font-weight-bold mb-1">
            <i class="fas fa-exclamation-circle text-primary mr-1"></i>
            Overpayment of KES {{ formatAmount(runningOverpayment) }}
          </p>
          <p class="text-muted mb-2">
            The amount paid exceeds the subscription fee. Load the difference to the customer's wallet?
          </p>
          <div class="d-flex">
            <b-button
              variant="primary"
              size="sm"
              :disabled="loadingOverpayment"
              @click="loadOverpaymentToWallet"
            >
              <b-spinner v-if="loadingOverpayment" class="mr-1" />
              Load KES {{ formatAmount(runningOverpayment) }} to Wallet
            </b-button>
            <b-button
              variant="outline-secondary"
              size="sm"
              class="ml-2"
              @click="overpaymentHandled = true"
            >
              Skip
            </b-button>
          </div>
        </b-card>
      </div>

      <!-- Fully settled -->
      <div
        v-if="runningRemaining <= 0 && (runningOverpayment === 0 || overpaymentHandled)"
        class="text-center py-3"
      >
        <i class="fas fa-check-circle fa-2x text-secondary mb-2 d-block"></i>
        <p class="font-weight-bold mb-0">Payment complete.</p>
        <p class="text-muted mb-3">All payments have been recorded.</p>
        <b-button variant="outline-secondary" @click="closePaymentModal">
          Close
        </b-button>
      </div>

      </template><!-- /subscription guard -->
    </b-modal>

    <!-- ── PAY INSTALLMENT MODAL ── -->
    <b-modal
      ref="instPayModal"
      title="Pay Installment"
      size="sm"
      hide-footer
      @hidden="resetInstPayModal"
    >
      <template v-if="instPayTarget">
        <b-card class="mb-3 border-0 bg-light">
          <div class="d-flex justify-content-between mb-1">
            <span class="text-muted">Installment #{{ instPayTarget.installment_number }}</span>
            <span class="inst-status-badge" :class="`inst-status-badge--${instPayTarget.status}`">
              {{ instPayTarget.status }}
            </span>
          </div>
          <div class="d-flex justify-content-between">
            <span class="text-muted">Balance</span>
            <strong class="text-danger">KES {{ fmtInst(instPayTarget.balance) }}</strong>
          </div>
        </b-card>

        <b-form-group label="Payment Method *">
          <b-form-select
            v-model="instPayLine.payment_type_id"
            :options="paymentMethodsWithWallet"
          />
        </b-form-group>

        <b-form-group label="Amount (KES) *">
          <b-form-input
            v-model.number="instPayLine.amount"
            type="number"
            min="0.01"
            :max="instPayTarget.balance"
            :placeholder="fmtInst(instPayTarget.balance)"
          />
        </b-form-group>

        <div v-if="instPayIsWallet" class="alert alert-secondary py-2 mb-3">
          <i class="bi bi-wallet mr-1"></i>
          Available: <strong>KES {{ fmtInst(walletBalance) }}</strong>
        </div>

        <b-button
          variant="primary"
          block
          :disabled="instPayPosting || !instPayLine.payment_type_id || !instPayLine.amount"
          @click="submitInstPay"
        >
          <b-spinner v-if="instPayPosting" small class="mr-1" />
          Record Payment
        </b-button>
      </template>
    </b-modal>

    <!-- ── CREATE INSTALLMENT SCHEDULE MODAL ── -->
    <b-modal
      ref="createScheduleModal"
      title="Create Installment Schedule"
      size="md"
      hide-footer
      @hidden="resetCreateSchedule"
    >
      <p class="text-muted small mb-3">
        Add installment tranches. Total should match the subscription amount due.
      </p>

      <div
        v-for="(row, idx) in scheduleRows"
        :key="idx"
        class="d-flex flex-wrap align-items-center gap-2 mb-2"
      >
        <span class="text-muted" style="width:24px;text-align:center">#{{ idx + 1 }}</span>
        <date-range-picker
          :single-date-picker="true" :auto-apply="true" :ranges="false" :append-to-body="true"
          :date-range="{ startDate: row.due_date ? new Date(row.due_date) : null }"
          @update="val => row.due_date = val && val.startDate ? formatPickerValue(val.startDate) : ''"
          style="flex:1"
        >
          <template #input="picker">
            <span v-if="picker.startDate">{{ formatPickerDate(picker.startDate) }}</span>
            <span v-else class="text-muted small">Due date</span>
          </template>
        </date-range-picker>
        <b-form-input
          v-model.number="row.amount_due"
          type="number"
          min="0.01"
          placeholder="Amount"
          size="sm"
          style="width:120px"
        />
        <button
          class="btn btn-sm btn-outline-danger"
          :disabled="scheduleRows.length <= 1"
          @click="scheduleRows.splice(idx, 1)"
        >
          <i class="bi bi-x"></i>
        </button>
      </div>

      <!-- Totals check -->
      <div class="d-flex flex-wrap justify-content-between text-muted small mt-2 mb-3 px-1">
        <span>Schedule Total: <strong>KES {{ fmtInst(scheduleTotal) }}</strong></span>
        <span>Amount Due: <strong>KES {{ fmtInst(calculateTotal()) }}</strong></span>
        <span
          :class="Math.abs(scheduleTotal - calculateTotal()) < 0.01 ? 'text-success' : 'text-warning'"
        >
          {{ Math.abs(scheduleTotal - calculateTotal()) < 0.01 ? '✓ Balanced' : '⚠ Unbalanced' }}
        </span>
      </div>

      <div class="d-flex justify-content-between mb-3">
        <button class="btn btn-sm btn-outline-secondary" @click="addScheduleRow">
          <i class="bi bi-plus mr-1"></i>Add Installment
        </button>
        <b-button
          variant="primary"
          size="sm"
          :disabled="savingSchedule || !scheduleRows.length"
          @click="saveInstallmentSchedule"
        >
          <b-spinner v-if="savingSchedule" small class="mr-1" />
          Save Schedule
        </b-button>
      </div>
    </b-modal>

    <!-- ── ADD NOTE MODAL ── -->
    <b-modal
      ref="addNoteModal"
      title="Add Note"
      ok-title="Save Note"
      :ok-disabled="addingNote || !noteForm.note.trim()"
      @ok.prevent="submitNote"
      @hidden="resetNoteForm"
    >
      <b-form-group label="Note Type">
        <b-form-select
          v-model="noteForm.note_type"
          :options="noteTypeOptions"
        />
      </b-form-group>
      <b-form-group label="Note *">
        <b-form-textarea
          v-model="noteForm.note"
          rows="4"
          placeholder="Enter note…"
          :state="noteForm.note.trim().length > 0 ? null : false"
        />
      </b-form-group>
    </b-modal>

    <!-- Child modals -->
    <renew-subscription-modal ref="renewModal" :can-discount="can('subscriptions.discount')" @renewed="handleRenewed" />
    <cancel-subscription-modal ref="cancelModal" @cancelled="handleCancelled" />
    <member-statement-modal ref="statementModal" />

    <!-- Hidden thermal receipt — unchanged -->
    <div id="sub-thermal-receipt" style="display:none" v-if="subscription">
      <div class="th-receipt">
        <div class="th-center">
          <div class="th-co-name">{{ companyInfo.name }}</div>
          <div class="th-co-meta" v-if="companyInfo.address">{{ companyInfo.address }}</div>
          <div class="th-co-meta" v-if="companyInfo.phone">Tel: {{ companyInfo.phone }}</div>
          <div class="th-co-meta" v-if="companyInfo.email">{{ companyInfo.email }}</div>
          <div class="th-co-meta" v-if="companyInfo.krapin">PIN: {{ companyInfo.krapin }}</div>
        </div>
        <div class="th-div">================================</div>
        <div class="th-center th-type">SUBSCRIPTION RECEIPT</div>
        <div class="th-div">================================</div>
        <div class="th-row"><span>Receipt:</span><span>{{ receiptNumber }}</span></div>
        <div class="th-row"><span>Date:</span><span>{{ currentDateTime }}</span></div>
        <div class="th-div">================================</div>
        <div class="th-stitle">CUSTOMER</div>
        <div class="th-row"><span>Name:</span><span>{{ subscription.customer?.full_name || '—' }}</span></div>
        <div class="th-row" v-if="subscription.customer?.email">
          <span>Email:</span><span>{{ subscription.customer.email }}</span>
        </div>
        <div class="th-row" v-if="subscription.customer?.krapin">
          <span>PIN:</span><span>{{ subscription.customer.krapin }}</span>
        </div>
        <div class="th-div">--------------------------------</div>
        <div class="th-stitle">SUBSCRIPTION</div>
        <div class="th-row"><span>Ref:</span><span>{{ subscription.subscription_order || receiptNumber }}</span></div>
        <div class="th-row"><span>Package:</span><span>{{ subscription.package.name }}</span></div>
        <div class="th-row"><span>Duration:</span><span>{{ subscription.package.duration_value }} {{ subscription.package.duration_unit }}(s)</span></div>
        <div class="th-row"><span>Status:</span><span>{{ subscription.status.toUpperCase() }}</span></div>
        <div class="th-row"><span>Start:</span><span>{{ formatDate(subscription.start_date,'DD-MM-YYYY') }}</span></div>
        <div class="th-row"><span>End:</span><span>{{ formatDate(subscription.end_date,'DD-MM-YYYY') }}</span></div>
        <div class="th-div">================================</div>
        <div class="th-stitle">LINE ITEMS</div>
        <div class="th-row">
          <span>{{ subscription.package.name }}</span>
        </div>
        <div class="th-row">
          <span>{{ participantCount }} × KES {{ formatAmount(subscription.package.price) }}</span>
          <span>KES {{ formatAmount(grossSubtotal) }}</span>
        </div>
        <div v-if="discountAmount > 0" class="th-row">
          <span>Discount</span>
          <span>- KES {{ formatAmount(discountAmount) }}</span>
        </div>
        <div class="th-div">================================</div>
        <div class="th-stitle">PAYMENT SUMMARY</div>
        <div v-if="discountAmount > 0" class="th-row">
          <span>Gross Subtotal:</span>
          <span>KES {{ formatAmount(grossSubtotal) }}</span>
        </div>
        <div v-if="discountAmount > 0" class="th-row">
          <span>Discount:</span>
          <span>- KES {{ formatAmount(discountAmount) }}</span>
        </div>
        <div class="th-row">
          <span>Net Amount:</span>
          <span>KES {{ formatAmount(calculateTotal()) }}</span>
        </div>
        <template v-if="subscription.tax_snapshot && subscription.tax_snapshot.length">
          <div v-for="tax in subscription.tax_snapshot" :key="tax.id" class="th-row th-tax">
            <span>{{ tax.name }} ({{ tax.perc != null ? tax.perc : tax.rate }}%) [incl.]:</span>
            <span>KES {{ formatAmount(calculateTaxAmount(tax)) }}</span>
          </div>
        </template>
        <div class="th-div-thin">- - - - - - - - - - - - - - - -</div>
        <div class="th-row th-bold"><span>TOTAL:</span><span>KES {{ formatAmount(calculateTotal()) }}</span></div>
        <div v-if="latestPayment" class="th-row">
          <span>Paid via:</span>
          <span>{{ latestPayment.payment_method ? latestPayment.payment_method.toUpperCase() : '' }}</span>
        </div>
        <div id="_sub_thermal_fiscal"></div>
        <div class="th-div">================================</div>
        <div class="th-center">Thank you for your subscription!</div>
        <div class="th-center th-keep">KEEP FOR YOUR RECORDS</div>
        <div class="th-center" v-if="companyInfo.companysettings">
          {{ companyInfo.companysettings.receipt_footer_message }}
        </div>
        <div class="th-div">================================</div>
        <div class="th-center th-sys">{{ currentDateTime }}</div>
        <div class="th-cut"></div>
      </div>
    </div>

  </b-modal>
</template>

<script>
import moment from 'moment'
import DateRangePicker from 'vue2-daterange-picker/src/components/DateRangePicker'
import RenewSubscriptionModal  from './RenewSubscriptionModal.vue'
import CancelSubscriptionModal from './CancelSubscriptionModal.vue'
import MemberStatementModal from './modals/MemberStatementModal.vue'
import { mapGetters } from 'vuex'
import subscriptionsAdminMixin from '../../mixins/subscriptionsAdminMixin.js'

export default {
  name: 'SubscriptionDetail',
  mixins: [subscriptionsAdminMixin],
  components: { RenewSubscriptionModal, CancelSubscriptionModal, DateRangePicker, MemberStatementModal },

  data () {
    return {
      subscription:          null,
      loading:               false,
      companyInfo:           {},
      hasTims:               false,
      isFiscalReady:         false,
      fiscalLoadingStatus:   '',
      fiscalPollingInterval: null,
      pdfGenerating:         false,
      issuingCreditNote:     false,
      fiscalReceiptLoading:  false,

      // ── Payment modal state ───────────────────────────────────────────
      paymentMethods:     [],
      walletBalance:      0,
      walletPaymentTypeId: null, // the UUID of the 'wallet' payment_type record

      // Running totals (local, updated after each post)
      runningTotalPaid:   0,

      // Current payment line being entered
      paymentLine: {
        payment_type_id: null,
        amount:          null,
      },
      paymentErrors:   {},
      postingPayment:  false,
      newPayments:     [], // { method_label, amount } — payments added this session

      // Overpayment
      overpaymentHandled: false,
      loadingOverpayment: false,

      // ── Notes log ────────────────────────────────────────────────────────
      subscriptionNotes: [],
      noteForm: { note: '', note_type: 'general' },
      addingNote: false,
      noteTypeOptions: [
        { value: 'general',  text: 'General' },
        { value: 'renewal',  text: 'Renewal' },
        { value: 'payment',  text: 'Payment' },
        { value: 'other',    text: 'Other' },
      ],

      // ── Inline notes field editing (legacy single notes field) ────────────
      editingNotes: false,
      notesEdit:    '',
      savingNotes:  false,

      // ── Installments ──────────────────────────────────────────────────────
      installments:        [],
      installmentsLoading: false,
      installmentTotals:   { amount_due: 0, amount_paid: 0, balance: 0 },

      // Pay installment modal
      instPayTarget:  null,
      instPayLine:    { payment_type_id: null, amount: null },
      instPayPosting: false,

      // Create schedule modal
      scheduleRows:   [{ due_date: '', amount_due: null }],
      savingSchedule: false,
    }
  },

  computed: {
    ...mapGetters(['userDetails']),

    receiptNumber () {
      return `SUB-${(this.subscription?.id || 'XXXX').substring(0, 8).toUpperCase()}`
    },
    currentDateTime () {
      return new Date().toLocaleString('en-KE', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit',
      })
    },
    customerInitial () {
      const name = this.subscription?.customer?.full_name
      return name ? name.trim().charAt(0).toUpperCase() : '?'
    },
    daysActive () {
      if (!this.subscription) return 0
      return Math.max(0, Math.floor((new Date() - new Date(this.subscription.start_date)) / 86400000))
    },
    daysUntilExpiry () {
      if (!this.subscription || this.subscription.status !== 'active') return 0
      return Math.max(0, Math.ceil((new Date(this.subscription.end_date) - new Date()) / 86400000))
    },
    canGenerateFiscalReceipt () {
      const ft = this.subscription?.fiscal_transaction
      if (!ft) return true
      return ft.status === 'failed'
    },
    totalPaid () {
      if (!this.subscription?.payments) return 0
      return this.subscription.payments.reduce((s, p) => s + parseFloat(p.amount || 0), 0)
    },
    renewalCount () {
      return this.subscription?.renewals?.length || 0
    },

    participantCount () {
      return Math.max(1, parseInt(this.subscription?.participant_count || 1))
    },

    grossSubtotal () {
      if (!this.subscription?.package) return 0
      return parseFloat(this.subscription.package.price || 0) * this.participantCount
    },

    discountAmount () {
      return parseFloat(this.subscription?.discount_amount || 0)
    },
    latestPayment () {
      if (!this.subscription?.payments?.length) return null
      return this.subscription.payments[this.subscription.payments.length - 1]
    },

    // ── Payment modal computed ────────────────────────────────────────────
    isWalletSelected () {
      return this.walletPaymentTypeId &&
        this.paymentLine.payment_type_id === this.walletPaymentTypeId
    },

    paymentMethodsWithWallet () {
      // paymentMethods already includes wallet if it exists in payment_types
      // We just need a placeholder first option
      return [
        { value: null, text: '— Select method —' },
        ...this.paymentMethods,
      ]
    },
    // Server figure (first plan + renewals - payments - wallet), shared with
    // the list and the dashboards; the client-side sum is only a fallback.
    balanceDue () {
      const server = this.subscription?.balance_due
      if (server !== undefined && server !== null) return Math.max(0, parseFloat(server))
      return Math.max(0, this.calculateTotal() - this.totalPaid)
    },
    isUnderPaid () {
      return this.balanceDue > 0
    },
    runningRemaining () {
      return Math.max(0, this.calculateTotal() - this.runningTotalPaid)
    },
    runningOverpayment () {
      return Math.max(0, this.runningTotalPaid - this.calculateTotal())
    },

    instPayIsWallet () {
      return this.walletPaymentTypeId &&
        this.instPayLine.payment_type_id === this.walletPaymentTypeId
    },

    scheduleTotal () {
      return this.scheduleRows.reduce((s, r) => s + parseFloat(r.amount_due || 0), 0)
    },
  },

  watch: {
    userDetails: {
      immediate: true,
      handler (val) {
        if (val?.company) this.companyInfo = val.company
      },
    },
  },

  beforeDestroy () {
    if (this.fiscalPollingInterval) {
      clearInterval(this.fiscalPollingInterval)
      this.fiscalPollingInterval = null
    }
  },

  created () {
    this._imageMemCache = {}
  },

  methods: {
    formatPickerValue(d) {
      return moment(d).format('YYYY-MM-DD');
    },
    formatPickerDate(d) {
      if (!d) return '';
      return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    },
    // ── PUBLIC API ────────────────────────────────────────────────────────
    show (subscription) {
      this.subscription      = subscription
      this.subscriptionNotes = []
      this.installments      = []
      this.installmentTotals = { amount_due: 0, amount_paid: 0, balance: 0 }
      this.loadSubscription(subscription.id)
      if (this.can('subscriptions.manage_payments')) {
        this.loadInstallments(subscription.id)
      }
      this.$nextTick(() => {
        this.$refs.modal && this.$refs.modal.show()
      })
    },

    async loadSubscription (id) {
      this.loading = true
      try {
        const response = await axios.get(`/api/subscriptions/${id}`)
        this.subscription      = response.data
        this.subscriptionNotes = response.data.subscription_notes || []
        this.runningTotalPaid  = this.subscription.payments
          ?.reduce((s, p) => s + parseFloat(p.amount || 0), 0) ?? 0
      } catch (error) {
        this.$bvToast.toast(`Failed to load subscription: ${error}`, {
          title: 'Error', variant: 'danger',
        })
      } finally {
        this.loading = false
      }
    },

    // ── PAYMENT MODAL ─────────────────────────────────────────────────────

    async openPaymentModal () {
      this.newPayments        = []
      this.paymentLine        = { payment_type_id: null, amount: null }
      this.paymentErrors      = {}
      this.overpaymentHandled = false
      this.runningTotalPaid   = this.totalPaid

      await Promise.all([
        this.loadPaymentMethods(),
        this.loadWalletBalance(),
      ])

      this.$refs.paymentModal.show()
    },

    closePaymentModal () {
      this.$refs.paymentModal.hide()
    },

    resetPaymentModal () {
      if (this.newPayments.length) {
        this.loadSubscription(this.subscription.id)
      }
      this.newPayments        = []
      this.paymentLine        = { payment_type_id: null, amount: null }
      this.paymentErrors      = {}
      this.overpaymentHandled = false
      this.postingPayment     = false
    },

    async loadPaymentMethods () {
      if (this.paymentMethods.length) return
      try {
        const { data } = await axios.get('/api/payment-methods')
        this.paymentMethods = (data.data || data).map(m => ({
          value: m.id,
          text:  m.payment_method,
        }))
        const walletType = (data.data || data).find(
          m => m.payment_method?.toLowerCase() === 'wallet'
        )
        this.walletPaymentTypeId = walletType?.id || null
      } catch (e) { console.error('Failed to load payment methods', e) }
    },

    async loadWalletBalance () {
      try {
        const { data } = await axios.get(
          `/api/subscriptions/wallet/${this.subscription.customer_id}`
        )
        this.walletBalance = parseFloat(data.balance || 0)
      } catch {
        this.walletBalance = 0
      }
    },

    async postPaymentLine () {
      this.paymentErrors = {}

      if (!this.paymentLine.payment_type_id) {
        this.paymentErrors.payment_type_id = 'Select a payment method.'
        return
      }
      if (!this.paymentLine.amount || this.paymentLine.amount <= 0) {
        this.paymentErrors.amount = 'Enter a valid amount.'
        return
      }

      // Wallet balance check
      if (this.isWalletSelected && this.paymentLine.amount > this.walletBalance) {
        this.paymentErrors.amount = `Insufficient wallet balance. Available: KES ${this.formatAmount(this.walletBalance)}.`
        return
      }

      this.postingPayment = true
      try {
        let endpoint, payload

        if (this.isWalletSelected) {
          endpoint = `/api/beta-pay/${this.subscription.id}/payment/wallet`
          payload  = { amount: this.paymentLine.amount }
        } else {
          endpoint = `/api/beta-pay/${this.subscription.id}/payment`
          payload  = {
            payment_type_id: this.paymentLine.payment_type_id,
            amount:          this.paymentLine.amount,
          }
        }

        const { data } = await axios.post(endpoint, payload)

        const methodLabel = this.paymentMethods.find(
          m => m.value === this.paymentLine.payment_type_id
        )?.text || (this.isWalletSelected ? 'Wallet' : 'Payment')

        this.newPayments.push({
          method_label: methodLabel,
          amount:       this.paymentLine.amount,
        })

        this.runningTotalPaid   = data.total_paid
        this.overpaymentHandled = false

        if (data.wallet_balance !== undefined) {
          this.walletBalance = data.wallet_balance
        }

        this.paymentLine = { payment_type_id: null, amount: null }

      } catch (e) {
        this.$bvToast.toast(
          e.response?.data?.message || 'Failed to record payment.',
          { title: 'Error', variant: 'danger' }
        )
      } finally {
        this.postingPayment = false
      }
    },

    async loadOverpaymentToWallet () {
      this.loadingOverpayment = true
      try {
        await axios.post(
          `/api/beta-pay/${this.subscription.id}/payment/overpayment-to-wallet`,
          { amount: this.runningOverpayment }
        )
        this.$bvToast.toast(
          `KES ${this.formatAmount(this.runningOverpayment)} loaded to wallet.`,
          { title: 'Wallet Loaded', variant: 'success' }
        )
        this.overpaymentHandled = true
        this.walletBalance      = (this.walletBalance || 0) + this.runningOverpayment
      } catch (e) {
        this.$bvToast.toast(
          e.response?.data?.message || 'Failed to load overpayment.',
          { title: 'Error', variant: 'danger' }
        )
      } finally {
        this.loadingOverpayment = false
      }
    },

    // ── PRINT / PDF — unchanged from original ────────────────────────────
    printReceipt () {
      if (!this.subscription || this.hasTims) return
      this._checkTimsReadiness()
    },

    _checkTimsReadiness () {
      const ft = this.subscription?.fiscal_transaction
      if (ft?.control_serial || ft?.qr_url) { this._executePrint(); return }
      const id = this.subscription?.id
      if (!id) { this._executePrint(); return }
      this.hasTims = true
      let attempts = 0
      const MAX = 10
      this.fiscalLoadingStatus = 'Waiting for fiscal device…'
      this.fiscalPollingInterval = setInterval(async () => {
        attempts++
        try {
          const { data: { data: fd } } = await axios.get(`/api/fiscal/receipts/${id}`)
          const done  = fd.completed || fd.status === 'completed' || fd.success
          const hasQR = !!fd.qr_url?.trim()
          if (done && hasQR) {
            this._applyFiscalData(fd)
            clearInterval(this.fiscalPollingInterval); this.fiscalPollingInterval = null
            this.isFiscalReady = true; this.hasTims = false
            this.fiscalLoadingStatus = 'Fiscal data ready!'
            this._executePrint()
          } else if (fd.failed || fd.status === 'failed' || attempts >= MAX) {
            clearInterval(this.fiscalPollingInterval); this.fiscalPollingInterval = null
            this.hasTims = false
            this.fiscalLoadingStatus = attempts >= MAX ? 'Fiscal timeout — printing anyway' : 'Fiscal failed — printing without fiscal data'
            this._executePrint()
          } else {
            this.fiscalLoadingStatus = `Waiting for fiscal device… (${attempts}/${MAX})`
          }
        } catch (e) {
          console.error('[subscription] fiscal poll error:', e)
          if (attempts >= MAX) {
            clearInterval(this.fiscalPollingInterval); this.fiscalPollingInterval = null
            this.hasTims = false; this.fiscalLoadingStatus = 'Fiscal error — printing anyway'
            this._executePrint()
          }
        }
      }, 2000)
    },

    _applyFiscalData (fd) {
      if (!this.subscription) return
      const existing = this.subscription.fiscal_transaction || {}
      const merged = Object.assign({}, existing, {
        qr_url: fd.qr_url || existing.qr_url,
        control_serial: fd.control_serial || existing.control_serial,
        control_datetime: fd.control_datetime || existing.control_datetime,
        control_unit_info: fd.control_unit_info || existing.control_unit_info,
        control_invoice_number: fd.control_invoice_number || existing.control_invoice_number,
        raw_response: fd.raw_response || existing.raw_response,
      })
      this.$set(this.subscription, 'fiscal_transaction', merged)
    },

    _preloadImage (url) {
      if (this._imageMemCache[url]) return Promise.resolve(this._imageMemCache[url])
      return new Promise((resolve) => {
        const xhr = new XMLHttpRequest()
        xhr.open('GET', url, true); xhr.responseType = 'blob'
        xhr.onload = () => {
          const reader = new FileReader()
          reader.onloadend = () => { this._imageMemCache[url] = reader.result; resolve(reader.result) }
          reader.onerror = () => resolve(url)
          reader.readAsDataURL(xhr.response)
        }
        xhr.onerror = () => resolve(url); xhr.send()
      })
    },

    async _executePrint () {
      if (!this.subscription) return
      const receiptEl = document.getElementById('sub-thermal-receipt')
      if (!receiptEl) return
      const ft = this.subscription?.fiscal_transaction || {}
      let qrDataUrl = null
      if (ft.qr_url) {
        const proxied = `/api/proxy-logo?remote_url=${encodeURIComponent(ft.qr_url)}`
        qrDataUrl = await this._preloadImage(proxied)
      }
      const fiscalEl = document.getElementById('_sub_thermal_fiscal')
      if (fiscalEl) fiscalEl.innerHTML = this._buildThermalFiscalHtml(qrDataUrl)
      receiptEl.style.display = 'block'
      const pw = window.open('', '_blank')
      pw.document.write(`<!DOCTYPE html><html><head><title>Subscription Receipt</title><style>
@media print{@page{size:80mm auto;margin:0}body{margin:0;padding:0}}
body{font-family:Arial,sans-serif;font-size:13px;line-height:1.5;color:#000;background:#fff;margin:0;padding:10px;width:80mm}
.th-receipt{width:100%}.th-center{text-align:center}.th-co-name{font-size:15px;font-weight:700;margin-bottom:3px}
.th-co-meta{font-size:11px;margin-bottom:2px}.th-type{font-size:13px;font-weight:700;margin:5px 0;letter-spacing:.5px}
.th-div{margin:5px 0;font-size:11px}.th-div-thin{margin:3px 0;font-size:10px}
.th-stitle{font-weight:700;font-size:11px;margin:6px 0 3px;text-transform:uppercase;letter-spacing:.5px}
.th-row{display:flex;justify-content:space-between;margin-bottom:3px;font-size:12px}
.th-tax{font-size:11px;padding-left:8px}.th-bold{font-weight:700;font-size:13px}
.th-keep{font-weight:700;margin:4px 0;font-size:12px}.th-sys{font-size:10px;color:#666;margin-top:5px}
.th-cut{border-top:1px dashed #000;margin-top:18px}.th-fiscal{margin:7px 0;padding:5px;border:1px solid #000;font-size:11px}
.th-fiscal-title{font-weight:700;font-size:11px;margin-bottom:4px;text-transform:uppercase}
.th-qr{text-align:center;margin:7px 0}.th-qr img{width:86px;height:86px}
</style></head><body>${receiptEl.innerHTML}</body></html>`)
      pw.document.close()
      setTimeout(() => { pw.focus(); pw.print(); pw.close(); receiptEl.style.display = 'none' }, 250)
      this.$bvToast.toast('Receipt sent to printer', { title: 'Success', variant: 'success' })
      this.fiscalLoadingStatus = ''
    },

    _buildThermalFiscalHtml (qrDataUrl = null) {
      const ft = this.subscription?.fiscal_transaction
      if (!ft || (!ft.control_serial && !ft.control_unit_info)) return ''
      let h = `<div class="th-div">================================</div><div class="th-fiscal"><div class="th-fiscal-title">Fiscal / TIMS</div>`
      if (ft.control_datetime)        h += `<div class="th-row"><span>Date:</span><span>${this._esc(ft.control_datetime)}</span></div>`
      if (ft.control_invoice_number)  h += `<div class="th-row"><span>Invoice #:</span><span>${this._esc(ft.control_invoice_number)}</span></div>`
      if (ft.control_serial)          h += `<div class="th-row"><span>Serial:</span><span>${this._esc(ft.control_serial)}</span></div>`
      if (ft.control_unit_info)       h += `<div class="th-row"><span>CU Info:</span><span>${this._esc(ft.control_unit_info)}</span></div>`
      h += `</div>`
      if (ft.qr_url) {
        const imgSrc = qrDataUrl || `/api/proxy-logo?remote_url=${encodeURIComponent(ft.qr_url)}`
        h += `<div class="th-qr"><img src="${imgSrc}" alt="Fiscal QR"/></div>`
      }
      return h
    },

    async downloadPdf () {
      if (!this.subscription || this.pdfGenerating) return
      this.pdfGenerating = true
      try {
        const ft = this.subscription?.fiscal_transaction || {}
        let qrDataUrl = null
        if (ft.qr_url) {
          const proxied = `/api/proxy-logo?remote_url=${encodeURIComponent(ft.qr_url)}`
          qrDataUrl = await this._preloadImage(proxied)
        }
        const win = window.open('', '_blank')
        win.document.write(this._buildPdfHtml(qrDataUrl))
        win.document.close()
        setTimeout(() => { win.focus(); win.print() }, 600)
      } catch (e) {
        this.$bvToast.toast(`Failed to generate PDF: ${e.message}`, { title: 'Error', variant: 'danger' })
      } finally {
        this.pdfGenerating = false
      }
    },

    _buildPdfHtml (qrDataUrl = null) {
      const s   = this.subscription
      if (!s) return ''
      const co  = this.companyInfo || {}
      const pkg = s.package || {}
      const cust = s.customer || {}
      const ft  = s.fiscal_transaction || {}

      const gross    = this.grossSubtotal
      const discount = this.discountAmount
      const net      = this.calculateTotal()
      const qty      = this.participantCount
      const payment  = this.latestPayment

      const taxes = (s.tax_snapshot || []).map(t => ({
        name:   t.name || 'Tax',
        rate:   t.perc != null ? t.perc : (t.rate || 0),
        amount: this.calculateTaxAmount(t),
      }))

      const esc = v => this._esc(v)
      const fmt = v => this.formatAmount(v)
      const fd  = d => this.formatDate(d, 'DD MMM YYYY')

      const fiscalHtml = (ft.control_serial || ft.control_unit_info || ft.control_invoice_number) ? `
        <div class="pdf-section fiscal-box">
          <div class="pdf-stitle">Fiscal / TIMS</div>
          <table class="pdf-info"><tbody>
            ${ft.control_datetime       ? `<tr><td>Date/Time</td><td>${esc(ft.control_datetime)}</td></tr>` : ''}
            ${ft.control_invoice_number ? `<tr><td>Invoice #</td><td>${esc(ft.control_invoice_number)}</td></tr>` : ''}
            ${ft.control_serial         ? `<tr><td>Serial</td><td>${esc(ft.control_serial)}</td></tr>` : ''}
            ${ft.control_unit_info      ? `<tr><td>CU Info</td><td>${esc(ft.control_unit_info)}</td></tr>` : ''}
          </tbody></table>
          ${qrDataUrl ? `<div class="pdf-qr"><img src="${qrDataUrl}" alt="Fiscal QR"/></div>` : ''}
        </div>` : ''

      return `<!DOCTYPE html><html><head><meta charset="utf-8">
        <title>Subscription Receipt - ${esc(s.subscription_order || s.id)}</title>
        <style>
          *{box-sizing:border-box;margin:0;padding:0}
          body{font-family:Arial,sans-serif;font-size:12px;color:#222;background:#fff}
          .page{max-width:800px;margin:0 auto;padding:40px}
          @media print{.page{padding:20px}@page{size:A4;margin:15mm}}

          .pdf-header{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:16px;border-bottom:2px solid #222;margin-bottom:24px}
          .pdf-co-name{font-size:20px;font-weight:700}
          .pdf-co-meta{font-size:11px;color:#666;line-height:1.7;margin-top:4px}
          .pdf-doc-title{text-align:right}
          .pdf-doc-type{font-size:18px;font-weight:700;text-transform:uppercase;color:#222}
          .pdf-doc-ref{font-size:11px;color:#666;margin-top:4px}
          .pdf-doc-date{font-size:11px;color:#666}

          .pdf-meta-grid{display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-bottom:24px}
          .pdf-section{margin-bottom:20px}
          .pdf-stitle{font-size:10px;text-transform:uppercase;letter-spacing:.8px;color:#888;font-weight:700;margin-bottom:8px;border-bottom:1px solid #eee;padding-bottom:4px}
          .pdf-info{width:100%;border-collapse:collapse}
          .pdf-info td{padding:3px 0;vertical-align:top;font-size:12px}
          .pdf-info td:first-child{color:#666;width:42%;padding-right:8px}
          .pdf-info td:last-child{font-weight:500}

          .pdf-items{width:100%;border-collapse:collapse;margin-bottom:20px}
          .pdf-items th{background:#f5f5f5;padding:8px 10px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:.4px;color:#666;border-bottom:2px solid #ddd}
          .pdf-items th:nth-child(2),.pdf-items th:last-child{text-align:right}
          .pdf-items td{padding:10px;border-bottom:1px solid #eee;font-size:12px}
          .pdf-items td:nth-child(2),.pdf-items td:last-child{text-align:right}
          .pdf-items .sub-r td{font-size:11px;color:#666;padding:3px 10px}

          .pdf-totals{margin-left:auto;width:260px}
          .pdf-t-row{display:flex;justify-content:space-between;padding:4px 0;border-bottom:1px solid #f0f0f0;font-size:12px}
          .pdf-t-row.disc{color:#28a745}
          .pdf-t-row.tax{color:#666;font-size:11px}
          .pdf-t-row.grand{font-weight:700;font-size:15px;border-top:2px solid #222;border-bottom:none;padding-top:8px;margin-top:4px}

          .badge{display:inline-block;padding:2px 8px;border-radius:3px;font-size:10px;font-weight:700;text-transform:uppercase}
          .b-active{background:#d4edda;color:#155724}
          .b-expired{background:#e2e3e5;color:#383d41}
          .b-cancelled{background:#fff3cd;color:#856404}
          .b-past_due{background:#f8d7da;color:#721c24}

          .fiscal-box{border:1px solid #ccc;border-radius:4px;padding:12px;background:#fafafa}
          .pdf-qr{text-align:right;margin-top:8px}
          .pdf-qr img{width:100px;height:100px}
          .pdf-footer{margin-top:32px;border-top:1px solid #eee;padding-top:10px;text-align:center;font-size:10px;color:#999}
        </style>
        </head><body><div class="page">

        <div class="pdf-header">
          <div>
            <div class="pdf-co-name">${esc(co.name || '')}</div>
            <div class="pdf-co-meta">
              ${co.address ? esc(co.address) + '<br>' : ''}
              ${co.phone   ? 'Tel: ' + esc(co.phone) + '<br>' : ''}
              ${co.email   ? esc(co.email) + '<br>' : ''}
              ${co.krapin  ? 'PIN: ' + esc(co.krapin) : ''}
            </div>
          </div>
          <div class="pdf-doc-title">
            <div class="pdf-doc-type">Subscription Receipt</div>
            <div class="pdf-doc-ref">${esc(s.subscription_order || 'SUB-' + String(s.id || '').substring(0,8).toUpperCase())}</div>
            <div class="pdf-doc-date">Date: ${fd(s.created_at)}</div>
          </div>
        </div>

        <div class="pdf-meta-grid">
          <div class="pdf-section">
            <div class="pdf-stitle">Customer</div>
            <table class="pdf-info"><tbody>
              <tr><td>Name</td><td>${esc(cust.full_name || '—')}</td></tr>
              ${cust.phone  ? `<tr><td>Phone</td><td>${esc(cust.phone)}</td></tr>` : ''}
              ${cust.email  ? `<tr><td>Email</td><td>${esc(cust.email)}</td></tr>` : ''}
              ${cust.krapin ? `<tr><td>KRA PIN</td><td>${esc(cust.krapin)}</td></tr>` : ''}
            </tbody></table>
          </div>
          <div class="pdf-section">
            <div class="pdf-stitle">Subscription</div>
            <table class="pdf-info"><tbody>
              <tr><td>Reference</td><td>${esc(s.subscription_order || '—')}</td></tr>
              <tr><td>Status</td><td><span class="badge b-${esc(s.status)}">${esc(s.status)}</span></td></tr>
              <tr><td>Start</td><td>${fd(s.start_date)}</td></tr>
              <tr><td>End</td><td>${fd(s.end_date)}</td></tr>
              <tr><td>Duration</td><td>${esc(pkg.duration_value || '')} ${esc(pkg.duration_unit || '')}(s)</td></tr>
            </tbody></table>
          </div>
        </div>

        <div class="pdf-section">
          <div class="pdf-stitle">Line Items</div>
          <table class="pdf-items">
            <thead><tr>
              <th>Description</th><th>Unit Price</th><th>Qty</th><th>Amount</th>
            </tr></thead>
            <tbody>
              <tr>
                <td>${esc(pkg.name || '')}</td>
                <td>KES ${fmt(pkg.price)}</td>
                <td>${qty}${qty > 1 ? ' participants' : ''}</td>
                <td>KES ${fmt(gross)}</td>
              </tr>
              ${discount > 0 ? `<tr class="sub-r">
                <td colspan="3">Discount${s.discount_reason ? ' — ' + esc(s.discount_reason) : ''}</td>
                <td style="color:#28a745">−&nbsp;KES ${fmt(discount)}</td>
              </tr>` : ''}
            </tbody>
          </table>
        </div>

        <div class="pdf-totals">
          ${discount > 0 ? `
            <div class="pdf-t-row"><span>Gross Subtotal</span><span>KES ${fmt(gross)}</span></div>
            <div class="pdf-t-row disc"><span>Discount</span><span>−&nbsp;KES ${fmt(discount)}</span></div>
          ` : ''}
          <div class="pdf-t-row"><span>Net Amount</span><span>KES ${fmt(net)}</span></div>
          ${taxes.map(t => `
            <div class="pdf-t-row tax"><span>${esc(t.name)} (${t.rate}%) [incl.]</span><span>KES ${fmt(t.amount)}</span></div>
          `).join('')}
          <div class="pdf-t-row grand"><span>TOTAL</span><span>KES ${fmt(net)}</span></div>
          ${payment ? `<div class="pdf-t-row" style="margin-top:8px;font-size:11px;color:#666">
            <span>Paid via</span><span>${esc((payment.payment_method || '').toUpperCase())}</span>
          </div>` : ''}
        </div>

        ${fiscalHtml}

        <div class="pdf-footer">
          ${co.companysettings?.receipt_footer_message ? esc(co.companysettings.receipt_footer_message) + '<br>' : ''}
          Thank you for your subscription! &nbsp;·&nbsp; Generated ${new Date().toLocaleString('en-KE')}
        </div>
      </div></body></html>`
    },

    // ── SUBSCRIPTION ACTIONS — unchanged ─────────────────────────────────
    renewSubscription  () { this.$refs.renewModal.show(this.subscription) },
    cancelSubscription () { this.$refs.cancelModal.show(this.subscription) },
    handleRenewed      () { this.loadSubscription(this.subscription.id) },
    handleCancelled    () { this.loadSubscription(this.subscription.id) },

    async toggleAutoRenew () {
      try {
        await axios.put(`/api/subscriptions/${this.subscription.id}`, {
          is_auto_renew: !this.subscription.is_auto_renew,
        })
        this.$bvToast.toast(
          `Auto-renew ${this.subscription.is_auto_renew ? 'disabled' : 'enabled'}`,
          { title: 'Success', variant: 'success' }
        )
        this.loadSubscription(this.subscription.id)
      } catch {
        this.$bvToast.toast('Failed to update auto-renew', { title: 'Error', variant: 'danger' })
      }
    },

    async issueCreditNote () {
      if (this.issuingCreditNote) return
      const ok = await this.$bvModal.msgBoxConfirm(
        'Issue a fiscal credit note to reverse the receipt for this subscription? This cannot be undone.',
        { title: 'Issue Credit Note', okVariant: 'warning', okTitle: 'Issue Credit Note', cancelTitle: 'Cancel' }
      )
      if (!ok) return
      this.issuingCreditNote = true
      try {
        await axios.post(`/api/subscriptions/${this.subscription.id}/credit-note`)
        this.$bvToast.toast('Credit note queued successfully.', { title: 'Fiscal', variant: 'success' })
      } catch (e) {
        const msg = e.response?.data?.message || 'Failed to issue credit note.'
        this.$bvToast.toast(msg, { title: 'Error', variant: 'danger' })
      } finally {
        this.issuingCreditNote = false
      }
    },

    async generateFiscalReceipt () {
      if (this.fiscalReceiptLoading) return
      this.fiscalReceiptLoading = true
      try {
        const type = this.subscription?.customer?.krapin ? 'invoice' : 'sale'
        const { data } = await axios.post(`/api/subscriptions/${this.subscription.id}/fiscal-receipt`, { transaction_type: type })
        if (data.success) {
          this.$bvToast.toast('Fiscal receipt queued for processing.', { title: 'Fiscal', variant: 'success' })
          this.$set(this.subscription, 'fiscal_transaction', { status: 'pending', transaction_type: type })
        } else {
          this.$bvToast.toast(data.message || 'Failed to generate fiscal receipt.', { title: 'Error', variant: 'danger' })
        }
      } catch (e) {
        const msg = e.response?.data?.message || 'Failed to generate fiscal receipt.'
        this.$bvToast.toast(msg, { title: 'Error', variant: 'danger' })
      } finally {
        this.fiscalReceiptLoading = false
      }
    },

    viewCustomer () {
      window.location.href = `/customers?c_id=${this.subscription.customer_id}`
    },

    async copyMemberLink () {
      try {
        const { data } = await axios.get(`/api/subscriptions/customers/${this.subscription.customer_id}/member-link`)
        await navigator.clipboard.writeText(data.url)
        this.$bvToast.toast('Member app link copied', { variant: 'primary', solid: true, autoHideDelay: 2000 })
      } catch (e) {
        this.$bvToast.toast('Could not copy the link.', { title: 'Error', variant: 'danger' })
      }
    },

    async sendMemberLink () {
      try {
        await axios.post(`/api/subscriptions/customers/${this.subscription.customer_id}/member-link`)
        this.$bvToast.toast('Member app link sent by SMS', { variant: 'primary', solid: true, autoHideDelay: 2000 })
      } catch (e) {
        this.$bvToast.toast(e.response?.data?.message || 'SMS could not be sent.', { title: 'Error', variant: 'danger' })
      }
    },

    async deleteSubscription () {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        'Are you sure you want to delete this subscription? This action cannot be undone.',
        { title: 'Confirm Deletion', okVariant: 'danger', okTitle: 'Yes, Delete', cancelTitle: 'Cancel' }
      )
      if (!confirmed) return
      try {
        await axios.delete(`/api/subscriptions/${this.subscription.id}`)
        this.$bvToast.toast('Subscription deleted', { title: 'Success', variant: 'success' })
        this.$refs.modal.hide()
      } catch {
        this.$bvToast.toast('Failed to delete subscription', { title: 'Error', variant: 'danger' })
      }
    },

    // ── INSTALLMENTS ──────────────────────────────────────────────────────

    async loadInstallments (id) {
      const subId = id || this.subscription?.id
      if (!subId) return
      this.installmentsLoading = true
      try {
        const { data } = await axios.get(`/api/subscriptions/${subId}/installments`)
        this.installments     = data.installments || []
        this.installmentTotals = data.totals || { amount_due: 0, amount_paid: 0, balance: 0 }
      } catch (e) {
        this.$bvToast.toast('Failed to load installments.', { title: 'Error', variant: 'danger' })
      } finally {
        this.installmentsLoading = false
      }
    },

    openInstPayModal (installment) {
      this.instPayTarget = installment
      this.instPayLine   = { payment_type_id: null, amount: parseFloat(installment.balance).toFixed(2) }
      Promise.all([this.loadPaymentMethods(), this.loadWalletBalance()])
        .then(() => this.$refs.instPayModal.show())
    },

    resetInstPayModal () {
      this.instPayTarget  = null
      this.instPayLine    = { payment_type_id: null, amount: null }
      this.instPayPosting = false
    },

    async submitInstPay () {
      if (!this.instPayLine.payment_type_id || !this.instPayLine.amount) return
      this.instPayPosting = true
      try {
        const payload = this.instPayIsWallet
          ? { use_wallet: true, amount: this.instPayLine.amount }
          : { payment_type_id: this.instPayLine.payment_type_id, amount: this.instPayLine.amount }

        await axios.post(
          `/api/subscriptions/${this.subscription.id}/installments/${this.instPayTarget.id}/pay`,
          payload
        )

        this.$bvToast.toast('Payment recorded.', { title: 'Success', variant: 'success' })
        this.$refs.instPayModal.hide()
        this.loadInstallments()
        // Refresh the full subscription too (payments updated)
        this.loadSubscription(this.subscription.id)
      } catch (e) {
        this.$bvToast.toast(
          e.response?.data?.message || 'Failed to record payment.',
          { title: 'Error', variant: 'danger' }
        )
      } finally {
        this.instPayPosting = false
      }
    },

    openCreateScheduleModal () {
      const remaining = this.calculateTotal() - this.totalPaid
      this.scheduleRows = [{ due_date: '', amount_due: remaining > 0 ? parseFloat(remaining).toFixed(2) : null }]
      this.$refs.createScheduleModal.show()
    },

    resetCreateSchedule () {
      this.scheduleRows   = [{ due_date: '', amount_due: null }]
      this.savingSchedule = false
    },

    addScheduleRow () {
      this.scheduleRows.push({ due_date: '', amount_due: null })
    },

    async saveInstallmentSchedule () {
      const valid = this.scheduleRows.every(r => r.due_date && r.amount_due > 0)
      if (!valid) {
        this.$bvToast.toast('All installments need a due date and amount.', { title: 'Validation', variant: 'warning' })
        return
      }
      this.savingSchedule = true
      try {
        await axios.post(`/api/subscriptions/${this.subscription.id}/installments`, {
          installments: this.scheduleRows,
        })
        this.$bvToast.toast('Installment schedule saved.', { title: 'Success', variant: 'success' })
        this.$refs.createScheduleModal.hide()
        this.loadInstallments()
      } catch (e) {
        this.$bvToast.toast(
          e.response?.data?.message || 'Failed to save schedule.',
          { title: 'Error', variant: 'danger' }
        )
      } finally {
        this.savingSchedule = false
      }
    },

    fmtInst (val) {
      return parseFloat(val || 0).toLocaleString('en-KE', {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
      })
    },

    // ── NOTES LOG ────────────────────────────────────────────────────────
    openAddNoteModal (prefillType = 'general') {
      this.noteForm = { note: '', note_type: prefillType }
      this.$refs.addNoteModal.show()
    },
    resetNoteForm () {
      this.noteForm  = { note: '', note_type: 'general' }
      this.addingNote = false
    },
    async submitNote () {
      if (!this.noteForm.note.trim()) return
      this.addingNote = true
      try {
        const { data } = await axios.post(
          `/api/subscriptions/${this.subscription.id}/notes`,
          this.noteForm
        )
        this.subscriptionNotes.unshift(data.data)
        this.$refs.addNoteModal.hide()
        this.$bvToast.toast('Note added.', { title: 'Saved', variant: 'success' })
      } catch {
        this.$bvToast.toast('Failed to add note.', { title: 'Error', variant: 'danger' })
      } finally {
        this.addingNote = false
      }
    },

    // ── NOTES EDITING ─────────────────────────────────────────────────────
    startEditNotes () {
      this.notesEdit    = this.subscription.notes || ''
      this.editingNotes = true
    },
    cancelEditNotes () {
      this.editingNotes = false
      this.notesEdit    = ''
    },
    async saveNotes () {
      this.savingNotes = true
      try {
        await axios.put(`/api/subscriptions/${this.subscription.id}`, {
          notes: this.notesEdit,
        })
        this.$set(this.subscription, 'notes', this.notesEdit)
        this.editingNotes = false
        this.$bvToast.toast('Notes updated.', { title: 'Saved', variant: 'success' })
      } catch {
        this.$bvToast.toast('Failed to save notes.', { title: 'Error', variant: 'danger' })
      } finally {
        this.savingNotes = false
      }
    },

    // ── SESSION HELPERS ───────────────────────────────────────────────────
    sessionDuration (sess) {
      if (!sess.exited_at) return ''
      const mins = Math.round((new Date(sess.exited_at) - new Date(sess.entered_at)) / 60000)
      if (mins < 60) return `${mins}m`
      return `${Math.floor(mins / 60)}h ${mins % 60}m`
    },

    // ── HELPERS — unchanged ───────────────────────────────────────────────
    formatDate (date, format = 'DD-MM-YYYY') {
      return date ? moment(date).format(format) : '—'
    },
    formatAmount (amount) {
      return parseFloat(amount || 0).toFixed(2)
    },
    formatMessageType (type) {
      return String(type).replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    },
    calculateTaxAmount (tax) {
      // Prefer the stored amount (correct for discounted subscriptions).
      if (tax.amount != null && parseFloat(tax.amount) > 0) {
        return parseFloat(tax.amount)
      }
      // Legacy fallback: recalculate from amount_due and rate.
      const total = this.calculateTotal()
      const rate  = parseFloat(tax.perc ?? tax.rate ?? 0)
      return tax.type === 'inclusive'
        ? (total / (100 + rate)) * rate
        : (total * rate) / 100
    },
    _totalTaxAmount () {
      return (this.subscription?.tax_snapshot || [])
        .reduce((s, t) => s + this.calculateTaxAmount(t), 0)
    },
    calculateTotal () {
      if (!this.subscription) return 0
      // amount_due is frozen at creation to the net payable (after discount, taxes included).
      const due = parseFloat(this.subscription.amount_due || 0)
      if (due > 0) return due
      // Fallback for legacy records where amount_due was not stored.
      return parseFloat(this.subscription.package?.price || 0)
    },
    getStatusVariant (status) {
      return { active: 'success', expired: 'secondary', cancelled: 'warning', past_due: 'danger' }[status] || 'secondary'
    },
    _esc (str) {
      if (str == null) return ''
      return String(str)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;')
        .replace(/>/g, '&gt;').replace(/"/g, '&quot;')
    },
  },
}
</script>

<style scoped>
/* ── All original styles preserved exactly ── */
.sub-root, .sub-loading {
  --primary:    #fd4719;
  --primary-bg: rgba(253,71,25,.1);
  --red-bg:     rgba(220,53,69,.1);
  --radius:     7px;
}
.sub-loading { display:flex;flex-direction:column;align-items:center;justify-content:center;padding:80px 24px;gap:16px;color:var(--bs-secondary);font-size:14px; }
.sub-spinner { width:36px;height:36px;border:3px solid var(--bs-light);border-top-color:var(--bs-dark);border-radius:50%;animation:sub-spin .7s linear infinite; }
@keyframes sub-spin { to { transform:rotate(360deg); } }
.sub-root { font-size:14px;color:var(--bs-dark); }
.sub-header { display:flex;justify-content:space-between;align-items:center;padding:20px 24px 18px;border-bottom:1px solid var(--bs-light);background:var(--bs-light); }
.sub-header__eyebrow { display:block;font-size:10px;text-transform:uppercase;letter-spacing:1.5px;color:var(--bs-secondary);margin-bottom:3px; }
.sub-header__ref { font-size:19px;font-weight:700;color:var(--bs-dark);margin:0;letter-spacing:-0.3px; }
.sub-header__right { display:flex;align-items:center;gap:8px; }
.sub-status-chip { display:inline-block;padding:3px 11px;border-radius:20px;font-size:11px;font-weight:600;text-transform:capitalize; }
.sub-status-chip--sm { padding:2px 8px;font-size:10px; }
.sub-status-chip--active,.sub-status-chip--confirmed { background:var(--primary-bg);color:var(--primary); }
.sub-status-chip--expired,.sub-status-chip--checked_out { background:rgba(108,117,125,.1);color:#6c757d; }
.sub-status-chip--cancelled { background:rgba(108,117,125,.1);color:#6c757d; }
.sub-status-chip--past_due { background:rgba(220,53,69,.1);color:var(--bs-danger); }
.sub-status-chip--pending { background:rgba(220,53,69,.1);color:var(--bs-danger); }
.sub-icon-btn { width:33px;height:33px;border-radius:var(--radius);border:1px solid var(--bs-light);background:#fff;color:var(--bs-secondary);display:inline-flex;align-items:center;justify-content:center;font-size:13px;cursor:pointer;transition:background .12s,color .12s; }
.sub-icon-btn:hover { background:var(--bs-light);color:var(--bs-dark); }
.sub-icon-btn--close:hover { background:rgba(220,53,69,.1);color:var(--bs-danger);border-color:rgba(220,53,69,.3); }
.sub-icon-btn--loading { opacity:.65;cursor:not-allowed; }
.sub-action-group { display:flex;align-items:center;gap:6px; }
.sub-dropdown { border:1px solid var(--bs-light);border-radius:var(--radius);box-shadow:0 8px 28px rgba(0,0,0,.1);padding:5px 0;min-width:200px; }
.sub-dropdown__label { font-size:10px;text-transform:uppercase;letter-spacing:1px;color:var(--bs-secondary);padding:7px 16px 3px;font-weight:600; }
.sub-dropdown .dropdown-item { font-size:13px;padding:7px 16px;color:var(--bs-dark);display:flex;align-items:center;gap:9px; }
.sub-dropdown .dropdown-item i { width:14px;color:var(--bs-secondary); }
.sub-dropdown .dropdown-item:hover { background:var(--bs-light); }
.sub-dropdown .dropdown-item.text-danger { color:var(--bs-danger); }
.sub-dropdown .dropdown-item.text-danger i { color:var(--bs-danger); }
.sub-fiscal-bar { display:flex;align-items:center;gap:10px;padding:10px 24px;background:var(--primary-bg);border-bottom:1px solid rgba(253,71,25,.2);font-size:13px;color:var(--primary); }
.sub-warn-band { display:flex;align-items:center;gap:8px;padding:9px 24px;background:rgba(220,53,69,.1);border-bottom:1px solid rgba(220,53,69,.2);font-size:13px;color:var(--bs-danger); }
.sub-warn-band__badge { margin-left:8px;padding:1px 8px;border-radius:12px;background:rgba(220,53,69,.15);font-size:11px;font-weight:600; }
.sub-pkg-strip { display:flex;align-items:center;flex-wrap:wrap;gap:16px;padding:12px 24px;background:var(--bs-dark); }
.sub-pkg-strip__label { display:block;font-size:9px;text-transform:uppercase;letter-spacing:1.2px;color:rgba(255,255,255,.45);margin-bottom:2px; }
.sub-pkg-strip__value { font-size:13px;font-weight:600;color:#fff; }
.sub-pkg-strip__value--accent { color:rgba(255,255,255,.9); }
.sub-pkg-strip__sep { color:rgba(255,255,255,.2);font-size:14px; }
.sub-body { display:flex; }
.sub-body__main { flex:1;padding:20px 24px;border-right:1px solid var(--bs-light);display:flex;flex-direction:column;gap:16px;min-width:0; }
.sub-body__aside { width:270px;flex-shrink:0;padding:20px 18px;background:var(--bs-light);display:flex;flex-direction:column;gap:14px; }
.sub-card { border:1px solid var(--bs-light);border-radius:var(--radius);overflow:hidden;background:#fff; }
.sub-card__header { display:flex;justify-content:space-between;align-items:center;padding:9px 14px;border-bottom:1px solid var(--bs-light);background:var(--bs-light); }
.sub-card__label { font-size:10px;text-transform:uppercase;letter-spacing:1px;color:var(--bs-secondary);font-weight:600; }
.sub-guest-block__name { font-size:15px;font-weight:600;color:var(--bs-dark);margin-bottom:5px; }
.sub-fields-grid { display:grid;grid-template-columns:1fr 1fr;gap:0;padding:4px 0; }
.sub-field { padding:9px 14px;border-bottom:1px solid #f8fafc; }
.sub-field--full { grid-column:1 / -1; }
.sub-field__key { display:block;font-size:10px;text-transform:uppercase;letter-spacing:0.5px;color:var(--bs-secondary);margin-bottom:2px; }
.sub-field__val { font-size:13px;color:var(--bs-dark);font-weight:500; }
.sub-field__val--mono { font-size:12px; }
.sub-toggle-badge { display:inline-block;padding:2px 9px;border-radius:12px;font-size:10px;font-weight:700;text-transform:uppercase; }
.sub-toggle-badge--on { background:var(--primary-bg);color:var(--primary); }
.sub-toggle-badge--off { background:rgba(108,117,125,.1);color:#6c757d; }
.sub-payment-row { display:flex;justify-content:space-between;align-items:center;padding:10px 14px;border-bottom:1px solid #f8fafc;gap:12px; }
.sub-payment-row:last-child { border-bottom:none; }
.sub-payment-row--alt { background:#fafafa; }
.sub-payment-row__left { flex:1;min-width:0; }
.sub-payment-row__right { display:flex;align-items:center;gap:8px;flex-shrink:0; }
.sub-payment-row__ref { display:block;font-size:13px;font-weight:600; }
.sub-payment-row__date { display:block;font-size:11px;color:var(--bs-secondary);margin-top:2px; }
.sub-payment-row__amount { font-size:13px;font-weight:600;color:var(--bs-dark); }
.sub-method-badge { display:inline-block;padding:2px 8px;border-radius:12px;font-size:10px;font-weight:600;text-transform:uppercase;background:rgba(108,117,125,.1);color:#6c757d; }
.sub-method-badge--sms { background:rgba(108,117,125,.15);color:#495057; }
.sub-sms-row { display:flex;justify-content:space-between;align-items:flex-start;padding:10px 14px;border-bottom:1px solid #f8fafc;gap:10px; }
.sub-sms-row:last-child { border-bottom:none; }
.sub-sms-row--alt { background:#fafafa; }
.sub-sms-row__left { display:flex;flex-direction:column;gap:4px;flex-shrink:0; }
.sub-sms-row__right { display:flex;align-items:flex-start;gap:8px;flex:1;justify-content:flex-end; }
.sub-sms-row__msg { font-size:11.5px;color:var(--bs-secondary);text-align:right; }
.sub-empty-text { padding:16px 14px;font-size:13px;color:var(--bs-secondary);margin:0; }
.sub-stat-chips { display:flex;gap:8px; }
.sub-stat-chip { flex:1;border:1px solid var(--bs-light);border-radius:var(--radius);padding:11px 8px;text-align:center;background:#fff; }
.sub-stat-chip__val { display:block;font-size:20px;font-weight:700;color:var(--bs-dark);line-height:1;margin-bottom:4px; }
.sub-stat-chip__val--warn { color:var(--bs-danger); }
.sub-stat-chip__lbl { display:block;font-size:10px;text-transform:uppercase;letter-spacing:0.8px;color:var(--bs-secondary); }
.sub-payment-stack { padding:12px 14px 0; }
.sub-payment-line { display:flex;justify-content:space-between;align-items:center;padding:5px 0;font-size:13px; }
.sub-payment-line--tax { font-size:12px;color:var(--bs-secondary);padding-left:8px; }
.sub-payment-line--total .sub-payment-line__val { font-weight:700;font-size:14px; }
.sub-payment-line--paid .sub-payment-line__val { color:var(--primary); }
.sub-payment-line--balance-due .sub-payment-line__val { color:var(--bs-danger); }
.sub-payment-line__label { color:var(--bs-secondary); }
.sub-payment-line__val { font-weight:600;color:var(--bs-dark); }
.sub-payment-line__val--due { color:var(--bs-danger) !important; }
.sub-payment-divider { height:1px;background:var(--bs-light);margin:4px 0; }
#sub-thermal-receipt { display:none !important; }

/* ── NEW: balance chip ── */
.sub-balance-chip {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  background: rgba(220,53,69,.1);
  color: var(--bs-danger);
}
.sub-balance-chip--sm { padding: 1px 7px; font-size: 10px; }

/* ── NEW: underpaid warning band ── */
.sub-underpaid-band {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 24px;
  background: rgba(220,53,69,.07);
  border-bottom: 1px solid rgba(220,53,69,.15);
  font-size: 13px;
  color: var(--bs-danger);
}
.sub-underpaid-band__btn {
  margin-left: auto;
  padding: 2px 12px;
  border-radius: 20px;
  border: 1px solid var(--bs-danger);
  background: transparent;
  color: var(--bs-danger);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background .12s;
}
.sub-underpaid-band__btn:hover {
  background: rgba(220,53,69,.1);
}

/* ── NEW: card header action button ── */
.sub-card__action-btn {
  padding: 2px 10px;
  border-radius: 20px;
  border: 1px solid var(--bs-secondary);
  background: transparent;
  color: var(--bs-dark);
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: background .12s;
}
.sub-card__action-btn:hover { background: var(--bs-light); }
.sub-card__action-btn--muted { color: var(--bs-secondary); border-color: var(--bs-light); }

/* ── Installments ── */
.inst-totals-bar {
  display: flex;
  gap: 16px;
  padding: 8px 14px;
  background: #f8f9fa;
  border-bottom: 1px solid var(--bs-light);
  font-size: 12px;
  color: var(--bs-secondary);
}
.inst-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  border-bottom: 1px solid #f8fafc;
  gap: 12px;
}
.inst-row:last-child { border-bottom: none; }
.inst-row--alt { background: #fafafa; }
.inst-row--overdue { background: rgba(220,53,69,.04); }
.inst-row__left {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}
.inst-row__right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.inst-row__num { font-size: 12px; font-weight: 700; color: var(--bs-dark); }
.inst-row__date { font-size: 11px; color: var(--bs-secondary); }
.inst-row__notes { font-size: 11px; color: var(--bs-secondary); font-style: italic; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 200px; }
.inst-row__amounts { font-size: 12px; display: flex; align-items: center; gap: 2px; }
.inst-row__paid { font-weight: 600; color: #198754; }
.inst-row__due { color: var(--bs-secondary); }
.inst-row__sep { color: var(--bs-light); }
.inst-status-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
}
.inst-status-badge--pending  { background: rgba(108,117,125,.12); color: #6c757d; }
.inst-status-badge--partial  { background: rgba(255,193,7,.15);   color: #856404; }
.inst-status-badge--paid     { background: rgba(25,135,84,.12);   color: #198754; }
.inst-status-badge--overdue  { background: rgba(220,53,69,.12);   color: #dc3545; }
.inst-pay-btn { font-size: 11px; padding: 1px 10px; border-radius: 20px; }

/* ── Notes log ── */
.sub-note-row {
  padding: 10px 14px;
  border-bottom: 1px solid #f8fafc;
}
.sub-note-row:last-child { border-bottom: none; }
.sub-note-row__body { font-size: 13px; color: var(--bs-dark); margin-bottom: 5px; white-space: pre-wrap; }
.sub-note-row__meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

/* ── Sessions ── */
.sub-session-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 9px 14px;
  border-bottom: 1px solid #f8fafc;
  gap: 12px;
}
.sub-session-row:last-child { border-bottom: none; }
.sub-session-row__left { display: flex; flex-direction: column; gap: 2px; flex: 1; min-width: 0; }
.sub-session-row__right { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }

/* ── Mobile ── */
@media (max-width: 767.98px) {
  .sub-header { flex-direction:column;align-items:flex-start;gap:10px;padding:14px 16px; }
  .sub-header__right { flex-wrap:wrap;width:100%; }
  .sub-action-group { margin-left:auto; }
  .sub-fiscal-bar, .sub-warn-band, .sub-underpaid-band { padding:9px 16px;flex-wrap:wrap; }
  .sub-pkg-strip { padding:12px 16px;gap:10px 14px; }
  .sub-pkg-strip__sep { display:none; }
  .sub-body { flex-direction:column; }
  .sub-body__main { padding:14px 12px;border-right:none; }
  .sub-body__aside { width:100%;padding:14px 12px; }
  .sub-fields-grid { grid-template-columns:1fr; }
  .sub-payment-row, .inst-row, .sub-session-row, .sub-sms-row { flex-wrap:wrap; }
  .sub-payment-row__right, .inst-row__right, .sub-session-row__right { flex-shrink:1;flex-wrap:wrap; }
  .sub-sms-row { flex-direction:column; }
  .sub-sms-row__left { flex-direction:row;align-items:center; }
  .sub-sms-row__right { justify-content:flex-start;width:100%; }
  .sub-sms-row__msg { text-align:left; }
  .inst-totals-bar { flex-wrap:wrap;gap:6px 16px; }
  .inst-row__notes { max-width:100%; }
}
</style>

<style>
.sub-modal-dialog { max-width: 960px !important; }
</style>
